import moment from "moment";
import React, {
  ElementType,
  FC,
  PropsWithChildren,
  ReactElement,
  ReactNode,
} from "react";
import { Paginator } from "./paginator";

interface GroupedListType {
  data: { [key: string]: any }[];
  role: "admin" | "client";
  onItemClick?: (item?: any) => void;
  hideKey?: boolean;
  pages?: {
    total: number;
    selectPage: (page: number) => void;
  };
  groupBy: {
    key: string;
    order?: "asc" | "desc";
    isDate: boolean;
  };
  jsx?: (item: any) => ReactNode;
  component?: ElementType;
  actionButtonOnClick?: (e: Event, item: any) => void;
}

const groupBy = function (xs: any, key: string, isDate: boolean = true) {
  xs = xs.map((i: any) => ({
    ...i,
    grouped_key: isDate
      ? moment.utc(i[key]).format("YYYY-MM-DD")
      : String(i[key]),
  }));

  return xs.reduce((rv: any, x: any) => {
    (rv[x["grouped_key"]] = rv[x["grouped_key"]] || []).push(x);
    return rv;
  }, {});
};

const orderBy = (order: "asc" | "desc", obj: any) => {
  return order == "desc"
    ? Object.keys(obj).sort().reverse()
    : Object.keys(obj).sort();
};

export const GroupedList: FC<PropsWithChildren<GroupedListType>> = ({
  data,
  role,
  onItemClick,
  pages = {
    total: 1,
    selectPage: (_: number) => {},
  },
  groupBy: { key, order = "desc", isDate },
  component: Component,
  children,
  hideKey = false,
  actionButtonOnClick,
}) => {
  const list = groupBy(data, key, isDate);
  return (
    <div className="flex flex-col">
      {orderBy(order, list).map((key: string, index: number) => {
        return (
          <div key={index} className={"w-full block flex-1"}>
            {!hideKey && (
              <span className="font-bold lg:text-lg text-sm text-gray-500 block mt-2">
                {isDate ? moment.utc(key).format("YYYY-MM-DD") : key}
              </span>
            )}
            <div className="flex flex-col">
              {list[key].map((i: any, index: number) => {
                return Component ? (
                  <Component
                    key={index}
                    role={role}
                    item={i}
                    actionButtonOnClick={actionButtonOnClick}
                    onClick={onItemClick ? () => onItemClick(i) : undefined}
                  />
                ) : children ? (
                  <div key={index}>
                    {React.cloneElement(children as ReactElement, { item: i })}
                  </div>
                ) : null;
              })}
            </div>
          </div>
        );
      })}
      {pages && pages.total > 1 ? (
        <div className="w-full flex justify-end items-end mt-2">
          <Paginator
            pages={pages.total}
            selectPage={pages.selectPage as (page: number) => void}
          />
        </div>
      ) : null}
    </div>
  );
};
